import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Package, ArrowRightLeft, Check, RefreshCw, MapPin, User,
  ListChecks, Send, X, Trash2, CheckCircle2, Zap, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import BarcodeInput from '@/components/BarcodeInput';
import ScanFeedbackOverlay from '@/components/ScanFeedbackOverlay';
import apiClient from '@/services/apiClient';
import { LockedTransferNote } from '@/components/wms/LockedTransferNote';

const BinToBinTransfer = () => {
  const navigate = useNavigate();
  const binInputRef = useRef(null);
  const usnInputRef = useRef(null);
  const staffInputRef = useRef(null);

  // Staff session (multi-staff, mirrors FG Put Away)
  const [staff, setStaff] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [staffScanValue, setStaffScanValue] = useState('');
  const [isValidatingStaff, setIsValidatingStaff] = useState(false);
  const [showChangeStaff, setShowChangeStaff] = useState(false);

  // Transfer flow
  const [targetBin, setTargetBin] = useState(null);
  const [targetBinInput, setTargetBinInput] = useState('');
  const [usnInput, setUsnInput] = useState('');
  const [step, setStep] = useState('bin'); // 'bin' | 'batch'
  const [scanFeedback, setScanFeedback] = useState(null);
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [totalTransferred, setTotalTransferred] = useState(0);
  const [fromLockedCount, setFromLockedCount] = useState(0);

  // SKU lock (established from target bin's existing SKU, or first scanned USN)
  const [lockedSku, setLockedSku] = useState(null);
  const [lockedDesign, setLockedDesign] = useState('');
  const [lockedShade, setLockedShade] = useState('');
  const [eligibleSkuUsns, setEligibleSkuUsns] = useState(null); // Set | null
  const [eligibleLoadFailed, setEligibleLoadFailed] = useState(false);

  // Batch queue
  const [batchQueue, setBatchQueue] = useState([]);
  const [batchRejected, setBatchRejected] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const rejectedSet = useMemo(() => new Set(batchRejected.map((r) => r.usn)), [batchRejected]);

  const focusStaff = useCallback(() => setTimeout(() => staffInputRef.current?.focus(), 80), []);
  const focusBin = useCallback(() => setTimeout(() => binInputRef.current?.focus(), 80), []);
  const focusUsn = useCallback(() => setTimeout(() => usnInputRef.current?.focus(), 80), []);

  useEffect(() => {
    if (!sessionStarted || showChangeStaff) focusStaff();
    else if (step === 'bin') focusBin();
    else if (step === 'batch') focusUsn();
  }, [step, sessionStarted, showChangeStaff, focusStaff, focusBin, focusUsn]);

  const showFeedback = (type, message) => {
    setScanFeedback({ id: Date.now() + Math.random(), type, message });
  };

  // === STAFF ===
  const handleStaffScan = async (e) => {
    e.preventDefault();
    const id = staffScanValue.trim();
    if (!id || isValidatingStaff) return;
    if (staffList.some((s) => s.staff_id === id)) {
      toast.info('Staff already added');
      setStaffScanValue('');
      focusStaff();
      return;
    }
    setIsValidatingStaff(true);
    try {
      const resp = await apiClient.post('/wms/fg/put-away/clearance/validate-staff', { staff_id: id });
      const member = { staff_id: resp.data.staff_id, staff_name: resp.data.staff_name };
      setStaffList((prev) => [...prev, member]);
      setStaff((prev) => prev || member);
      setStaffScanValue('');
      showFeedback('success', resp.data.staff_name);
      focusStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid Staff ID');
      setStaffScanValue('');
      focusStaff();
    } finally {
      setIsValidatingStaff(false);
    }
  };

  const handleRemoveStaff = (id) => {
    setStaffList((prev) => {
      const next = prev.filter((s) => s.staff_id !== id);
      setStaff(next[0] || null);
      return next;
    });
  };

  const handleStartSession = () => {
    if (staffList.length === 0) {
      toast.error('Add at least one staff');
      return;
    }
    setStaff(staffList[0]);
    setSessionStarted(true);
    setShowChangeStaff(false);
  };

  const handleChangeStaff = () => {
    setShowChangeStaff(true);
    setStaffList([]);
    setStaff(null);
    setSessionStarted(false);
    setStaffScanValue('');
  };

  // === SKU GUARD PREFETCH ===
  const prefetchEligibleUsns = useCallback(async (sku) => {
    if (!sku) return;
    setEligibleSkuUsns(null);
    setEligibleLoadFailed(false);
    try {
      const resp = await apiClient.get('/wms/fg/put-away/bin-transfer/sku-eligible-usns', { params: { sku } });
      setEligibleSkuUsns(new Set(resp.data.usns || []));
    } catch (error) {
      console.error('Failed to prefetch eligible USNs:', error);
      setEligibleLoadFailed(true);
    }
  }, []);

  // === BIN SCAN ===
  const handleBinScan = async (value) => {
    if (!value || value.trim() === '') {
      setErrors({ ...errors, bin: 'Bin code is required' });
      showFeedback('error', 'Bin Code Required');
      return;
    }
    try {
      const response = await apiClient.get(
        `/wms/fg/put-away/bin-transfer/validate-bin/${encodeURIComponent(value.trim())}`,
      );
      if (!response.data.valid) {
        setErrors({ ...errors, bin: response.data.message });
        showFeedback('error', 'Invalid Bin');
        setTargetBinInput('');
        return;
      }
      const bin = response.data;
      setTargetBin(bin);
      setTargetBinInput('');
      setErrors({ ...errors, bin: null });
      showFeedback('success', `Bin: ${value}`);

      // If the bin already stores a SKU, lock to it + arm the guard now.
      const existingSku = (bin.unique_skus && bin.unique_skus[0]) || null;
      if (existingSku) {
        setLockedSku(existingSku);
        setLockedDesign(bin.existing_design?.design_name || '');
        setLockedShade(bin.existing_design?.shade || '');
        prefetchEligibleUsns(existingSku);
      } else {
        setLockedSku(null);
        setLockedDesign('');
        setLockedShade('');
        setEligibleSkuUsns(null);
        setEligibleLoadFailed(false);
      }
      setStep('batch');
    } catch (error) {
      console.error('Bin validation error:', error);
      setErrors({ ...errors, bin: 'Failed to validate bin' });
      showFeedback('error', 'Validation Error');
      setTargetBinInput('');
    }
  };

  // === USN SCAN (queue) ===
  const handleUsnScan = async (value) => {
    const usn = (value || '').trim();
    if (!usn) {
      showFeedback('error', 'USN Required');
      return;
    }
    setUsnInput('');

    if (batchQueue.includes(usn)) {
      toast.warning(`USN ${usn} already in queue`, { duration: 1200, position: 'top-center' });
      showFeedback('error', 'Already in Queue');
      focusUsn();
      return;
    }
    if (rejectedSet.has(usn)) {
      toast.warning(`USN ${usn} is rejected — delete it first to re-add`, { duration: 1800, position: 'top-center' });
      focusUsn();
      return;
    }

    // SKU already locked → pure client-side queue with guard
    if (lockedSku) {
      if (eligibleSkuUsns instanceof Set && eligibleSkuUsns.size > 0 && !eligibleSkuUsns.has(usn)) {
        const label = (lockedDesign || '') + (lockedShade ? ` / ${lockedShade}` : '') || lockedSku;
        toast.error(`USN ${usn} does NOT belong to the locked SKU (${label}). One SKU per bin.`, { duration: 3200, position: 'top-center' });
        showFeedback('error', 'Wrong SKU');
        focusUsn();
        return;
      }
      setBatchQueue((prev) => [...prev, usn]);
      showFeedback('success', 'Queued');
      focusUsn();
      return;
    }

    // No SKU lock yet (empty target bin) → first USN establishes it via validate-usn
    setIsProcessing(true);
    try {
      const v = await apiClient.get(`/wms/fg/put-away/bin-transfer/validate-usn/${encodeURIComponent(usn)}`);
      if (!v.data.valid) {
        toast.error(v.data.message || 'Invalid USN', { position: 'top-center' });
        showFeedback('error', 'Invalid USN');
        focusUsn();
        return;
      }
      if (v.data.current_bin === targetBin.bin_code) {
        toast.error(`USN is already in bin ${targetBin.bin_code}`, { position: 'top-center' });
        showFeedback('error', 'Already in Target');
        focusUsn();
        return;
      }
      const sku = v.data.item_barcode || v.data.sku_code;
      setLockedSku(sku);
      setLockedDesign(v.data.design_name || '');
      setLockedShade(v.data.shade || '');
      prefetchEligibleUsns(sku);
      setBatchQueue((prev) => [...prev, usn]);
      showFeedback('success', `Locked: ${v.data.design_name || sku}`);
      focusUsn();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to validate USN', { position: 'top-center' });
      showFeedback('error', 'Error');
      focusUsn();
    } finally {
      setIsProcessing(false);
    }
  };

  // === SUBMIT BATCH ===
  const handleSubmitBatch = async () => {
    if (batchQueue.length === 0 || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const resp = await apiClient.post('/wms/fg/put-away/bin-transfer/transfer-batch', {
        target_bin_code: targetBin.bin_code,
        expected_sku: lockedSku || '',
        usns: batchQueue,
        staff_id: staff?.staff_id || '',
        staff_name: staff?.staff_name || '',
        staff_ids: staffList.map((s) => s.staff_id),
        staff_index: totalTransferred,
      });
      const placedList = resp.data.placed || [];
      const rejectedList = resp.data.rejected || [];

      setTotalTransferred((prev) => prev + placedList.length);
      setFromLockedCount((prev) => prev + (resp.data.from_locked?.length || 0));
      setTargetBin((prev) => (prev ? { ...prev, current_qty: resp.data.target_bin_qty } : prev));

      // Keep only rejected USNs in the queue so operator can delete & continue
      setBatchQueue(rejectedList.map((r) => r.usn));
      setBatchRejected(rejectedList);

      if (placedList.length > 0) {
        toast.success(`Transferred ${placedList.length} USN${placedList.length === 1 ? '' : 's'} to ${targetBin.bin_code}`, { duration: 2200 });
      }
      if (rejectedList.length > 0) {
        toast.error(`${rejectedList.length} rejected — review below and delete`, { duration: 3500 });
      }
      focusUsn();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Batch submit failed. Retry.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRejected = (usn) => {
    setBatchQueue((prev) => prev.filter((u) => u !== usn));
    setBatchRejected((prev) => prev.filter((r) => r.usn !== usn));
    focusUsn();
  };

  const handleDiscard = () => {
    if (batchQueue.length > 0) {
      const ok = window.confirm(`Discard ${batchQueue.length} queued USN${batchQueue.length === 1 ? '' : 's'}? They will NOT be transferred.`);
      if (!ok) return;
    }
    setBatchQueue([]);
    setBatchRejected([]);
    focusUsn();
    toast.info('Batch discarded.');
  };

  const handleChangeBin = () => {
    setTargetBin(null);
    setLockedSku(null);
    setLockedDesign('');
    setLockedShade('');
    setEligibleSkuUsns(null);
    setEligibleLoadFailed(false);
    setBatchQueue([]);
    setBatchRejected([]);
    setStep('bin');
  };

  const handleNewTransfer = () => {
    handleChangeBin();
    setTotalTransferred(0);
    setFromLockedCount(0);
    setTargetBinInput('');
    setUsnInput('');
  };

  const pendingCount = batchQueue.length - batchRejected.length;

  // === STAFF SCAN SCREEN ===
  if (!sessionStarted || showChangeStaff) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex flex-col" data-testid="bin-to-bin-transfer">
              {scanFeedback && (
        <ScanFeedbackOverlay
          key={scanFeedback.id}
          type={scanFeedback.type}
          message={scanFeedback.message}
          onComplete={() => setScanFeedback(null)}
        />
      )}
        <header className="bg-[#1A1F2E] text-[#F9FAFB] px-4 pt-12 md:pt-0 min-h-14 py-2 md:py-0 flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => (showChangeStaff ? setShowChangeStaff(false) : navigate('/wms/fg'))}
            className="text-[#F9FAFB] hover:bg-white/10 p-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="font-semibold text-sm">{showChangeStaff ? 'Change Staff' : 'Bin to Bin Transfer'}</span>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="w-16 h-16 rounded-full bg-[#1A1F2E]/10 flex items-center justify-center mb-5">
            <User className="w-8 h-8 text-[#1A1F2E]" />
          </div>
          <h2 className="text-lg font-bold text-[#1A1F2E] mb-1">{showChangeStaff ? 'Switch Staff' : 'Scan Staff ID(s)'}</h2>
          <p className="text-sm text-[#5A5F6E] mb-6">Add one or more staff sharing this transfer session</p>
          <form onSubmit={handleStaffScan} className="w-full max-w-xs space-y-3">
            <Input
              ref={staffInputRef}
              value={staffScanValue}
              onChange={(e) => setStaffScanValue(e.target.value)}
              placeholder="Scan staff ID badge..."
              className="text-lg h-12 border-[#E5DFD0] bg-white font-mono text-center"
              autoFocus
              disabled={isValidatingStaff}
              data-testid="staff-scan-input"
            />
            <Button
              type="submit"
              disabled={!staffScanValue.trim() || isValidatingStaff}
              className="w-full h-11 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              data-testid="staff-scan-submit"
            >
              {isValidatingStaff ? 'Validating...' : '+ Add Staff'}
            </Button>
          </form>
          {staffList.length > 0 && (
            <div className="w-full max-w-xs mt-4 space-y-2" data-testid="transfer-staff-list">
              <p className="text-xs text-[#5A5F6E] font-medium">{staffList.length} staff added:</p>
              <div className="flex flex-wrap gap-2">
                {staffList.map((s) => (
                  <div
                    key={s.staff_id}
                    className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-full pl-3 pr-1 py-1"
                    data-testid={`transfer-staff-chip-${s.staff_id}`}
                  >
                    <span className="text-xs font-medium text-green-800">{s.staff_name}</span>
                    <span className="text-[10px] text-green-600">({s.staff_id})</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveStaff(s.staff_id)}
                      className="w-5 h-5 rounded-full bg-green-100 hover:bg-red-200 flex items-center justify-center text-green-700 hover:text-red-700 text-xs"
                      data-testid={`transfer-staff-remove-${s.staff_id}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <Button
                onClick={handleStartSession}
                disabled={staffList.length === 0}
                className="w-full h-11 bg-[#1A1F2E] hover:bg-[#2a2f3e] text-white"
                data-testid="transfer-start-session-btn"
              >
                Start Session ({staffList.length})
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // === MAIN ===
  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="bin-to-bin-transfer">
            {scanFeedback && (
        <ScanFeedbackOverlay
          key={scanFeedback.id}
          type={scanFeedback.type}
          message={scanFeedback.message}
          onComplete={() => setScanFeedback(null)}
        />
      )}

      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg pt-12 md:pt-0">
        <div className="max-w-7xl mx-auto px-4 md:px-6 min-h-16 py-2 md:py-0 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg')}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold font-display flex items-center gap-2">
                <ArrowRightLeft className="w-6 h-6" />
                Bin to Bin Transfer
              </h1>
              <button
                onClick={handleChangeStaff}
                className="text-xs text-[#E8DCC8] hover:text-white flex items-center gap-1 truncate"
                data-testid="change-staff-btn"
              >
                <User className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">
                  {staffList.length > 1 ? `${staffList.length} staff` : `${staff?.staff_name} (${staff?.staff_id})`}
                </span>
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {totalTransferred > 0 && (
              <span
                className="bg-emerald-500 text-white px-2 py-0.5 rounded-full font-bold text-xs flex items-center gap-1"
                data-testid="total-transferred-count"
              >
                <CheckCircle2 className="w-3 h-3" />
                {totalTransferred}
              </span>
            )}
            {(targetBin || totalTransferred > 0) && (
              <Button
                onClick={handleNewTransfer}
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                data-testid="new-transfer-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                New
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* STEP 1: TARGET BIN */}
        {step === 'bin' && (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                <span className="bg-[#2D5F4F] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                Scan Target Bin
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">Scan or enter the bin code where you want to transfer USNs</p>
              <BarcodeInput
                ref={binInputRef}
                value={targetBinInput}
                onChange={setTargetBinInput}
                onScan={handleBinScan}
                placeholder="Scan target bin code (e.g., H01-A-01-L1-B01)"
                error={errors.bin}
                data-testid="bin-input"
              />
            </CardContent>
          </Card>
        )}

        {/* STEP 2: BATCH */}
        {step === 'batch' && targetBin && (
          <>
            {/* Target Bin Info */}
            <Card className="bg-blue-50 border-blue-200" data-testid="target-bin-banner">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Target Bin</p>
                      <p className="text-xl font-bold text-blue-800" data-testid="target-bin-code">{targetBin.bin_code}</p>
                      <p className="text-xs text-blue-600">{targetBin.location_path}</p>
                      {lockedSku && (
                        <p className="text-xs text-emerald-700 mt-1 font-medium" data-testid="locked-sku-label">
                          🔒 {lockedDesign || lockedSku}{lockedShade ? ` / ${lockedShade}` : ''}
                        </p>
                      )}
                      <LockedTransferNote lock={targetBin.lock} testId="target-bin-lock-note" />
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-blue-600">Current Items</p>
                    <p className="text-2xl font-bold text-blue-800" data-testid="target-bin-qty">{targetBin.current_qty}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleChangeBin}
                      className="text-blue-600 hover:text-blue-700 mt-1"
                      data-testid="change-bin-btn"
                    >
                      Change Bin
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {fromLockedCount > 0 && (
              <p className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-2" data-testid="from-locked-note">
                {fromLockedCount} USN{fromLockedCount === 1 ? '' : 's'} moved out of locked bins, transfer allowed.
              </p>
            )}

            {/* SKU guard status */}
            {lockedSku && eligibleSkuUsns === null && !eligibleLoadFailed && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="sku-guard-loading">
                ⏳ Loading SKU guard… wrong-SKU scans will be caught at submit until ready.
              </p>
            )}
            {eligibleLoadFailed && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="sku-guard-failed">
                ⚠ SKU guard unavailable. Wrong-SKU scans will be flagged at submit.
              </p>
            )}
            {lockedSku && eligibleSkuUsns instanceof Set && eligibleSkuUsns.size > 0 && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="sku-guard-active">
                ✓ SKU guard active — wrong-SKU scans hard-blocked ({eligibleSkuUsns.size} eligible USNs).
              </p>
            )}

            {/* Scan USN */}
            <Card className="border-[#E5DFD0] border-2 border-dashed">
              <CardHeader className="pb-3">
                <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                  <span className="bg-[#2D5F4F] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                  Scan USNs to Queue
                  <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full" data-testid="batch-mode-badge">
                    <Zap className="w-3 h-3" /> Fast Batch
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Scan USNs to move them to bin <strong>{targetBin.bin_code}</strong>. They queue instantly — press Submit to transfer.
                </p>
                <BarcodeInput
                  ref={usnInputRef}
                  value={usnInput}
                  onChange={setUsnInput}
                  onScan={handleUsnScan}
                  placeholder="Scan USN — instant queue…"
                  error={errors.usn}
                  disabled={isProcessing || isSubmitting}
                  data-testid="usn-input"
                />
                {isProcessing && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Locking SKU…
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Summary tiles */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-4 text-center">
                  <ListChecks className="w-6 h-6 mx-auto text-emerald-600 mb-1" />
                  <p className="text-2xl font-bold text-emerald-800" data-testid="queue-count">{pendingCount}</p>
                  <p className="text-xs text-emerald-600">In Queue</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 text-center">
                  <Check className="w-6 h-6 mx-auto text-green-600 mb-1" />
                  <p className="text-2xl font-bold text-green-800">{totalTransferred}</p>
                  <p className="text-xs text-green-600">Transferred</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 text-center">
                  <Package className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                  <p className="text-2xl font-bold text-blue-800">{targetBin.current_qty}</p>
                  <p className="text-xs text-blue-600">In Target Bin</p>
                </CardContent>
              </Card>
            </div>

            {/* Batch queue + actions */}
            {(batchQueue.length > 0 || batchRejected.length > 0) && (
              <Card className="border-2 border-emerald-400 bg-white" data-testid="batch-active-panel">
                <CardContent className="p-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">
                      Batch Queue ({batchQueue.length})
                    </span>
                  </div>

                  <div className="max-h-64 overflow-y-auto space-y-1.5 border border-[#E5DFD0] rounded p-2 bg-[#FFFBF5]" data-testid="batch-queue-list">
                    {batchQueue.length === 0 ? (
                      <p className="text-xs text-[#5A5F6E] text-center py-2">No USNs in queue — scan to add.</p>
                    ) : (
                      batchQueue.map((u) => {
                        const reject = batchRejected.find((r) => r.usn === u);
                        return (
                          <div
                            key={u}
                            className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded text-xs font-mono ${reject ? 'bg-rose-50 border border-rose-200' : 'bg-emerald-50 border border-emerald-100'}`}
                            data-testid={`batch-queue-row-${u}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                {reject ? <X className="w-3 h-3 text-rose-600 flex-shrink-0" /> : <CheckCircle2 className="w-3 h-3 text-emerald-600 flex-shrink-0" />}
                                <span className={`truncate ${reject ? 'text-rose-800 line-through' : 'text-emerald-900'}`}>{u}</span>
                              </div>
                              {reject && (
                                <p className="text-[10px] text-rose-700 ml-4 mt-0.5 font-sans" data-testid={`batch-reject-reason-${u}`}>
                                  ⚠ {reject.reason}
                                </p>
                              )}
                            </div>
                            {reject && (
                              <button
                                type="button"
                                onClick={() => handleDeleteRejected(u)}
                                className="text-rose-600 hover:bg-rose-100 rounded p-1 flex-shrink-0"
                                data-testid={`batch-delete-${u}`}
                                aria-label={`Delete rejected USN ${u}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      onClick={handleSubmitBatch}
                      disabled={pendingCount === 0 || isSubmitting}
                      className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                      data-testid="batch-submit-btn"
                    >
                      {isSubmitting ? (
                        <span className="animate-pulse">Submitting…</span>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Submit ({pendingCount})
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleDiscard}
                      disabled={isSubmitting}
                      className="h-11 px-3 border-[#E5DFD0] text-[#5A5F6E]"
                      data-testid="batch-discard-btn"
                    >
                      Discard
                    </Button>
                  </div>

                  {batchRejected.length > 0 && (
                    <div className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <span>Delete each rejected USN above to clear the queue, or fix and re-scan.</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default BinToBinTransfer;
